import { useEffect, useState, useCallback, useMemo, useRef } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import Text from "../../../shared/ui/Typography/Text";
import InlineError from "../../../shared/ui/Field/InlineError";

import NotificationPreferenceField from "../ui/NotificationPreferenceField";
import {
  fetchNotificationPreferences,
  patchNotificationPreferences,
} from "../api/preferences";
import {
  disableWebPushOnServer,
  enableWebPushOnServer,
  fetchWebPushConfig,
  fetchWebPushSubscriptions,
  reconcileWebPushOnServer,
  revokeWebPushSubscription,
} from "../api/webPush";
import {
  WEB_PUSH_LOCAL_SUBSCRIPTION,
  WEB_PUSH_PERMISSION,
  WEB_PUSH_RECOVERY_REASON,
  disableWebPushLocally,
  explicitEnableWebPush,
  getNotificationPermissionState,
  inspectCurrentWebPushSubscription,
  isWebPushSupported,
  reconcileWebPushIfEligible,
} from "../../../pwa/webPush";

// NotificationPreferencesForm — feature widget. Owns local state for the
// preferences form lifecycle: load, edit, validate, submit, sync.
// NOTIFICATION-EMAIL-BOUNDARY-A1: only the in-app channel is editable for email.
// NOTIFICATION-WEB-PUSH-C2: separate Web Push group with explicit permission flow.

const IN_APP_FIELD = {
  name: "in_app_enabled",
  label: "Сповіщення у застосунку",
  description: "Показувати сповіщення в інтерфейсі Рієстро.",
};

const IN_APP_CATEGORY_FIELDS = [
  {
    name: "in_app_chat",
    label: "Повідомлення в чатах",
    description: "Сповіщення про нові повідомлення в чатах.",
  },
  {
    name: "in_app_followers",
    label: "Нові підписники",
    description: "Сповіщення, коли хтось підписується на ваш профіль.",
  },
];

const WEB_PUSH_CATEGORY_FIELDS = [
  {
    name: "web_push_company",
    label: "Компанія",
  },
  {
    name: "web_push_clients",
    label: "Клієнти",
  },
  {
    name: "web_push_deals",
    label: "Угоди",
  },
  {
    name: "web_push_viewings",
    label: "Перегляди",
  },
  {
    name: "web_push_tasks",
    label: "Задачі",
  },
  {
    name: "web_push_property_engagement",
    label: "Активність об’єктів",
  },
  {
    name: "web_push_complaints",
    label: "Скарги",
  },
  {
    name: "web_push_subscription",
    label: "Підписка та системні події",
  },
  {
    name: "web_push_chat",
    label: "Чати",
  },
  {
    name: "web_push_followers",
    label: "Нові підписники",
  },
];

const EDITABLE_KEYS = [
  IN_APP_FIELD.name,
  ...IN_APP_CATEGORY_FIELDS.map((field) => field.name),
  ...WEB_PUSH_CATEGORY_FIELDS.map((field) => field.name),
];

const DENIED_PERMISSION_GUIDANCE = [
  "Відкрийте налаштування сайту біля адресного рядка, дозвольте сповіщення та оновіть сторінку.",
  "Якщо сповіщення вимкнені в операційній системі, дозвольте їх для браузера або встановленого Рієстро в системних налаштуваннях.",
];

const messageFromError = (error) => {
  if (!error) return "Помилка з'єднання.";
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (typeof data === "string") return data;
    if (typeof data.detail === "string") return data.detail;
    const firstFieldErrors = Object.values(data).find(
      (v) => Array.isArray(v) && v.length > 0,
    );
    if (firstFieldErrors) return String(firstFieldErrors[0]);
  }
  return error.message || "Помилка з'єднання.";
};

const pickEditable = (data) => {
  const next = {};
  for (const key of EDITABLE_KEYS) {
    next[key] = Boolean(data && data[key]);
  }
  return next;
};

const buildPatchBody = (values, baseline) => {
  const diff = {};
  for (const key of EDITABLE_KEYS) {
    if (Boolean(values[key]) !== Boolean(baseline[key])) {
      diff[key] = Boolean(values[key]);
    }
  }
  return diff;
};

const shallowEqualEditable = (a, b) => {
  if (!a || !b) return false;
  for (const key of EDITABLE_KEYS) {
    if (Boolean(a[key]) !== Boolean(b[key])) return false;
  }
  return true;
};

/**
 * Non-prompting preferences-local reconcile probe.
 * Never requests permission, never subscribe()/unsubscribe(), never enable.
 */
async function checkCurrentBrowserReconcileState({
  serverWebPushEnabled,
  reconcileOnServer = reconcileWebPushOnServer,
}) {
  return reconcileWebPushIfEligible({
    isAuthenticated: true,
    serverWebPushEnabled: Boolean(serverWebPushEnabled),
    reconcileOnServer,
  });
}

function NotificationPreferencesForm() {
  const [baseline, setBaseline] = useState(null);
  const [values, setValues] = useState(null);
  const [prefsMeta, setPrefsMeta] = useState({
    web_push_enabled: false,
  });
  const [devices, setDevices] = useState([]);
  const [pushConfig, setPushConfig] = useState(null);
  const [permissionState, setPermissionState] = useState(
    () => getNotificationPermissionState(),
  );
  const [localSubscriptionState, setLocalSubscriptionState] = useState(
    WEB_PUSH_LOCAL_SUBSCRIPTION.CHECKING,
  );
  const [reconcileRecoveryReason, setReconcileRecoveryReason] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  // B1-09 / R1-1 — per-device revoke pending. A Set lets device A revoke
  // without freezing unrelated device B/C revoke controls.
  const [revokingDeviceIds, setRevokingDeviceIds] = useState(() => new Set());
  const [revokeConfirmDeviceId, setRevokeConfirmDeviceId] = useState(null);
  const [disableAllConfirmOpen, setDisableAllConfirmOpen] = useState(false);
  const [pushMessage, setPushMessage] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null);
  const permissionProbeRef = useRef(false);
  const initialReconcileCheckRef = useRef(false);

  const applyReconcileCheckResult = useCallback((result) => {
    if (!result) return;
    if (result.status === "needs_recovery") {
      setReconcileRecoveryReason(
        result.reason || WEB_PUSH_RECOVERY_REASON.SUBSCRIPTION_REVOKED,
      );
      return;
    }
    if (result.status === "reconciled") {
      setReconcileRecoveryReason(null);
      const sub =
        result.result
        && result.result.subscription
        && result.result.subscription.id != null
          ? result.result.subscription.id
          : null;
      if (sub != null) {
        setCurrentSubscriptionId(sub);
      }
    }
    // skipped/failed: do not falsely claim recovery.
  }, []);

  const runCurrentBrowserReconcileCheck = useCallback(async ({
    masterEnabled,
    localState,
  }) => {
    if (!masterEnabled) return null;
    if (getNotificationPermissionState() !== WEB_PUSH_PERMISSION.GRANTED) {
      return null;
    }
    if (localState !== WEB_PUSH_LOCAL_SUBSCRIPTION.PRESENT) {
      return null;
    }
    try {
      const result = await checkCurrentBrowserReconcileState({
        serverWebPushEnabled: true,
        reconcileOnServer: reconcileWebPushOnServer,
      });
      applyReconcileCheckResult(result);
      return result;
    } catch (_err) {
      // Non-blocking — never escalate background reconcile into fatal form error.
      return null;
    }
  }, [applyReconcileCheckResult]);

  // Permission probe + exactly one initial local inspection (non-blocking).
  // Never requestPermission / subscribe / unsubscribe / enable here.
  useEffect(() => {
    permissionProbeRef.current = false;
    setPermissionState(getNotificationPermissionState());
    let cancelled = false;
    inspectCurrentWebPushSubscription()
      .then((state) => {
        if (!cancelled) setLocalSubscriptionState(state);
      })
      .catch(() => {
        if (!cancelled) {
          setLocalSubscriptionState(WEB_PUSH_LOCAL_SUBSCRIPTION.UNAVAILABLE);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Primary required load — independent of local Push inspection.
  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);
    Promise.all([
      fetchNotificationPreferences(),
      fetchWebPushConfig().catch(() => ({ available: false, public_key: null })),
      fetchWebPushSubscriptions().catch(() => []),
    ])
      .then(([data, config, subscriptions]) => {
        if (cancelled) return;
        const editable = pickEditable(data);
        setBaseline(editable);
        setValues(editable);
        const masterOn = Boolean(data && data.web_push_enabled);
        setPrefsMeta({
          web_push_enabled: masterOn,
        });
        setPushConfig(config || { available: false, public_key: null });
        setDevices(Array.isArray(subscriptions) ? subscriptions : []);
        setPermissionState(getNotificationPermissionState());
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Optional reconcile only after local PRESENT + required prefs loaded.
  useEffect(() => {
    if (loadState !== "loaded") return undefined;
    if (!prefsMeta.web_push_enabled) return undefined;
    if (localSubscriptionState !== WEB_PUSH_LOCAL_SUBSCRIPTION.PRESENT) {
      return undefined;
    }
    if (getNotificationPermissionState() !== WEB_PUSH_PERMISSION.GRANTED) {
      return undefined;
    }
    if (initialReconcileCheckRef.current) return undefined;
    initialReconcileCheckRef.current = true;
    let cancelled = false;
    runCurrentBrowserReconcileCheck({
      masterEnabled: true,
      localState: localSubscriptionState,
    }).then(() => {
      // Result already applied inside helper; ignore after unmount.
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [
    loadState,
    prefsMeta.web_push_enabled,
    localSubscriptionState,
    runCurrentBrowserReconcileCheck,
  ]);

  const handleToggle = useCallback((name, checked) => {
    setValues((prev) => (prev ? { ...prev, [name]: checked } : prev));
    setSaveError(null);
    setSaveState((prev) => (prev === "saved" ? "idle" : prev));
  }, []);

  const dirty = useMemo(
    () => Boolean(baseline && values && !shallowEqualEditable(baseline, values)),
    [baseline, values],
  );

  const applyServerPushState = useCallback((payload) => {
    if (!payload) return;
    if (payload.preferences) {
      const editable = pickEditable(payload.preferences);
      setBaseline(editable);
      setValues(editable);
      setPrefsMeta({
        web_push_enabled: Boolean(payload.preferences.web_push_enabled),
      });
    }
    if (Array.isArray(payload.subscriptions)) {
      setDevices(payload.subscriptions);
    }
    if (payload.subscription && payload.subscription.id != null) {
      setCurrentSubscriptionId(payload.subscription.id);
    }
    setPermissionState(getNotificationPermissionState());
  }, []);

  const refreshLocalSubscriptionState = useCallback(async () => {
    const state = await inspectCurrentWebPushSubscription();
    setLocalSubscriptionState(state);
    return state;
  }, []);

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!values || !baseline || !dirty || saveState === "saving") return;

    setSaveError(null);
    setSaveState("saving");

    try {
      const updated = await patchNotificationPreferences(
        buildPatchBody(values, baseline),
      );
      const editable = pickEditable(updated);
      setBaseline(editable);
      setValues(editable);
      setPrefsMeta({
        web_push_enabled: Boolean(updated.web_push_enabled),
      });
      setSaveState("saved");
    } catch (err) {
      setSaveError(messageFromError(err));
      setSaveState("error");
    }
  };

  const handleReset = () => {
    if (!baseline || saveState === "saving") return;
    setValues(baseline);
    setSaveError(null);
    setSaveState((prev) => (prev === "saved" ? "idle" : prev));
  };

  const handleEnablePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    setPushError(null);
    setPushMessage(null);
    try {
      if (!values || !values.in_app_enabled) {
        setPushError(
          "Спочатку увімкніть сповіщення в застосунку, щоб активувати системні сповіщення.",
        );
        return;
      }
      const config = pushConfig || (await fetchWebPushConfig());
      setPushConfig(config);
      if (!config || !config.available || !config.public_key) {
        setPushError("Системні сповіщення не налаштовано в цьому середовищі.");
        return;
      }
      const payload = await explicitEnableWebPush({
        publicKey: config.public_key,
        enableOnServer: enableWebPushOnServer,
      });
      applyServerPushState(payload);
      setLocalSubscriptionState(WEB_PUSH_LOCAL_SUBSCRIPTION.PRESENT);
      setReconcileRecoveryReason(null);
      setPushMessage("Системні сповіщення увімкнено.");
    } catch (err) {
      setPermissionState(getNotificationPermissionState());
      await refreshLocalSubscriptionState();
      if (err && err.code === "denied") {
        setPushError(DENIED_PERMISSION_GUIDANCE.join(" "));
      } else if (err && err.code === "unsupported") {
        setPushError("Цей браузер не підтримує системні сповіщення.");
      } else {
        setPushError(messageFromError(err));
      }
    } finally {
      setPushBusy(false);
      setPermissionState(getNotificationPermissionState());
    }
  };

  const handleDisablePush = async () => {
    // Global disable may remain mutually exclusive with in-flight device revokes.
    if (pushBusy || revokingDeviceIds.size > 0) return;
    setPushBusy(true);
    setDisableAllConfirmOpen(false);
    setPushError(null);
    setPushMessage(null);
    try {
      const result = await disableWebPushLocally({
        disableOnServer: disableWebPushOnServer,
      });
      applyServerPushState(result.serverResult);
      setReconcileRecoveryReason(null);
      if (result.localUnsubscribed) {
        setLocalSubscriptionState(WEB_PUSH_LOCAL_SUBSCRIPTION.MISSING);
      } else {
        await refreshLocalSubscriptionState();
      }
      if (result.localError) {
        setPushMessage(
          "Доставку вимкнено на сервері. Не вдалося скасувати підписку браузера локально. "
          + "Дозвіл браузера може лишатися наданим.",
        );
      } else {
        setPushMessage(
          "Доставку вимкнено. Дозвіл браузера може лишатися наданим.",
        );
      }
    } catch (err) {
      setPushError(messageFromError(err));
    } finally {
      setPushBusy(false);
      setPermissionState(getNotificationPermissionState());
    }
  };

  const handleRevokeDeviceRequest = (deviceId) => {
    // Global pushBusy may block new revoke starts; an in-flight revoke on
    // another device must NOT block opening confirm for this device.
    if (pushBusy || deviceId == null) return;
    if (revokingDeviceIds.has(deviceId)) return;
    setRevokeConfirmDeviceId(deviceId);
    setDisableAllConfirmOpen(false);
    setPushError(null);
  };

  const handleRevokeDeviceConfirm = async () => {
    const deviceId = revokeConfirmDeviceId;
    if (deviceId == null || pushBusy) return;
    if (revokingDeviceIds.has(deviceId)) return;
    setRevokeConfirmDeviceId(null);
    setRevokingDeviceIds((prev) => {
      const next = new Set(prev);
      next.add(deviceId);
      return next;
    });
    setPushError(null);
    try {
      await revokeWebPushSubscription(deviceId);
      const next = await fetchWebPushSubscriptions();
      setDevices(Array.isArray(next) ? next : []);
      setPushMessage("Пристрій відкликано.");
      const localState = await refreshLocalSubscriptionState();
      await runCurrentBrowserReconcileCheck({
        masterEnabled: Boolean(prefsMeta.web_push_enabled),
        localState,
      });
    } catch (err) {
      setPushError(messageFromError(err));
    } finally {
      setRevokingDeviceIds((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    }
  };

  if (loadState === "loading") {
    return (
      <div className="feature-notification-preferences__state">
        <Spinner size="lg" label="Завантаження налаштувань" />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="feature-notification-preferences__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити налаштування"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
        />
      </div>
    );
  }

  if (!values) return null;

  const saving = saveState === "saving";
  const supported = isWebPushSupported();
  const serverAvailable = Boolean(pushConfig && pushConfig.available);
  const masterEnabled = Boolean(prefsMeta.web_push_enabled);
  // Global preference/enable/disable controls may wait on any revoke; device
  // rows themselves use per-id pending (see device list below).
  const anyDeviceRevoking = revokingDeviceIds.size > 0;
  const anyPushBusy = pushBusy || anyDeviceRevoking;
  const categoriesDisabled = saving || anyPushBusy || !masterEnabled;

  let pushStatusBlock = null;
  if (!supported) {
    pushStatusBlock = (
      <Text as="p" size="sm" tone="secondary">
        Цей браузер не підтримує системні сповіщення.
      </Text>
    );
  } else if (!serverAvailable) {
    pushStatusBlock = (
      <Text as="p" size="sm" tone="secondary">
        Системні сповіщення не налаштовано в цьому середовищі.
      </Text>
    );
  } else if (permissionState === WEB_PUSH_PERMISSION.DENIED) {
    pushStatusBlock = (
      <div
        className="feature-notification-preferences__denied-guidance"
        data-web-push-permission-denied="true"
        role="status"
      >
        <Text as="p" size="sm" tone="secondary">
          Дозвіл на сповіщення заблоковано в браузері або операційній системі.
          Рієстро не може змінити цей дозвіл самостійно.
        </Text>
        <Text as="p" size="sm" tone="secondary">
          {DENIED_PERMISSION_GUIDANCE[0]}
        </Text>
        <Text as="p" size="sm" tone="secondary">
          {DENIED_PERMISSION_GUIDANCE[1]}
        </Text>
      </div>
    );
  }

  const permissionDenied = permissionState === WEB_PUSH_PERMISSION.DENIED;
  const permissionGranted = permissionState === WEB_PUSH_PERMISSION.GRANTED;
  const permissionDefault = permissionState === WEB_PUSH_PERMISSION.DEFAULT;

  // Recoverable current-browser states while master delivery is on.
  // DEFAULT is recoverable via explicit click (requestPermission only there).
  // DENIED is not recoverable in-app.
  const showRecoveryHint = supported
    && serverAvailable
    && masterEnabled
    && !permissionDenied
    && (
      permissionDefault
      || localSubscriptionState === WEB_PUSH_LOCAL_SUBSCRIPTION.MISSING
      || Boolean(reconcileRecoveryReason)
    );

  // Explicit Web Push CTA state machine — never show generic Enable + Disable
  // as opposing peer primary controls during recovery.
  const canPromptOrRecover = supported
    && serverAvailable
    && !permissionDenied;
  const showMasterEnableCta = canPromptOrRecover && !masterEnabled;
  const showDeviceRecoverCta = canPromptOrRecover && showRecoveryHint;
  const browserHealthy = masterEnabled
    && permissionGranted
    && localSubscriptionState === WEB_PUSH_LOCAL_SUBSCRIPTION.PRESENT
    && !reconcileRecoveryReason;
  const showHealthyDisable = browserHealthy;
  const showGlobalDisable = masterEnabled
    && !browserHealthy
    && (showRecoveryHint || permissionDenied);

  let recoveryCopy = (
    "Дозвіл надано, але підписку браузера не знайдено. Натисніть "
    + "«Відновити сповіщення на цьому пристрої», щоб відновити доставку."
  );
  if (permissionDefault) {
    recoveryCopy = (
      "Дозвіл на сповіщення ще не надано в цьому браузері. Натисніть "
      + "«Відновити сповіщення на цьому пристрої», щоб дозволити доставку."
    );
  } else if (localSubscriptionState !== WEB_PUSH_LOCAL_SUBSCRIPTION.MISSING) {
    if (reconcileRecoveryReason === WEB_PUSH_RECOVERY_REASON.ENDPOINT_CONFLICT) {
      recoveryCopy = (
        "Підписку цього браузера потрібно зареєструвати повторно. "
        + "Натисніть «Відновити сповіщення на цьому пристрої», щоб відновити доставку."
      );
    } else if (
      reconcileRecoveryReason === WEB_PUSH_RECOVERY_REASON.SUBSCRIPTION_REVOKED
    ) {
      recoveryCopy = (
        "Підписку цього браузера відкликано на сервері. Натисніть "
        + "«Відновити сповіщення на цьому пристрої», щоб відновити доставку."
      );
    }
  }

  const formatDeviceTiming = (device) => {
    const stamp = device && (device.last_seen_at || device.updated_at);
    if (!stamp) return null;
    try {
      const date = new Date(stamp);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleString("uk-UA", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch (_err) {
      return null;
    }
  };

  const renderDeviceLabel = (device) => {
    const label = device.device_label || `Пристрій #${device.id}`;
    const isCurrent =
      currentSubscriptionId != null
      && String(device.id) === String(currentSubscriptionId);
    const timing = formatDeviceTiming(device);
    return (
      <div>
        <Text as="div" size="sm" tone="primary">
          {label}
          {isCurrent ? (
            <span
              className="feature-notification-preferences__device-current"
              data-device-current="true"
            >
              Цей пристрій
            </span>
          ) : null}
        </Text>
        <Text as="div" size="xs" tone="secondary">
          {device.is_active ? "Активний" : "Неактивний"}
          {timing ? ` · ${timing}` : null}
        </Text>
      </div>
    );
  };

  const activeDevices = devices.filter((device) => Boolean(device && device.is_active));
  const inactiveDevices = devices.filter((device) => Boolean(device && !device.is_active));

  return (
    <form
      className="feature-notification-preferences__form"
      data-feature="notification-preferences-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <section className="feature-notification-preferences__group">
        <header className="feature-notification-preferences__group-header">
          <Text as="div" size="sm" weight="semibold" tone="primary">
            Канали доставки
          </Text>
          <Text as="div" size="xs" tone="secondary">
            Керуйте сповіщеннями в застосунку та системними сповіщеннями окремо.
          </Text>
        </header>
        <NotificationPreferenceField
          name={IN_APP_FIELD.name}
          label={IN_APP_FIELD.label}
          description={IN_APP_FIELD.description}
          checked={Boolean(values[IN_APP_FIELD.name])}
          disabled={saving || anyPushBusy}
          onToggle={handleToggle}
        />
        {IN_APP_CATEGORY_FIELDS.map((field) => (
          <NotificationPreferenceField
            key={field.name}
            name={field.name}
            label={field.label}
            description={field.description}
            checked={Boolean(values[field.name])}
            disabled={
              saving
              || anyPushBusy
              || !values.in_app_enabled
            }
            onToggle={handleToggle}
          />
        ))}
      </section>

      <section className="feature-notification-preferences__group">
        <header className="feature-notification-preferences__group-header">
          <Text as="div" size="sm" weight="semibold" tone="primary">
            Системні сповіщення
          </Text>
          <Text as="div" size="xs" tone="secondary">
            Системні сповіщення з’являються на вашому пристрої, навіть коли Рієстро не відкрито.
          </Text>
        </header>

        {pushStatusBlock}

        <Text as="p" size="xs" tone="secondary" data-web-push-delivery-status="true">
          Стан дозволу браузера:{" "}
          {permissionState === WEB_PUSH_PERMISSION.UNSUPPORTED
            ? "не підтримується"
            : permissionState === WEB_PUSH_PERMISSION.GRANTED
              ? "надано"
              : permissionState === WEB_PUSH_PERMISSION.DENIED
                ? "заблоковано"
                : "не надано"}
          {masterEnabled ? " · доставка увімкнена" : " · доставка вимкнена"}
        </Text>

        {!values.in_app_enabled ? (
          <Text as="p" size="sm" tone="secondary">
            Спочатку увімкніть сповіщення в застосунку, щоб активувати системні сповіщення.
          </Text>
        ) : null}

        <div className="feature-notification-preferences__push-actions">
          {showMasterEnableCta ? (
            <button
              type="button"
              className="feature-notification-preferences__push-enable"
              onClick={handleEnablePush}
              disabled={anyPushBusy || !values.in_app_enabled}
              data-web-push-enable="true"
            >
              {pushBusy ? (
                <Spinner size="sm" label="Увімкнення системних сповіщень" />
              ) : (
                "Увімкнути системні сповіщення"
              )}
            </button>
          ) : null}

          {showDeviceRecoverCta ? (
            <button
              type="button"
              className="feature-notification-preferences__push-enable"
              onClick={handleEnablePush}
              disabled={anyPushBusy || !values.in_app_enabled}
              data-web-push-enable="true"
              data-web-push-recover="true"
            >
              {pushBusy ? (
                <Spinner size="sm" label="Відновлення системних сповіщень" />
              ) : (
                "Відновити сповіщення на цьому пристрої"
              )}
            </button>
          ) : null}

          {showHealthyDisable ? (
            <button
              type="button"
              className="feature-notification-preferences__reset-button"
              onClick={() => void handleDisablePush()}
              disabled={anyPushBusy}
              data-web-push-disable="true"
            >
              Вимкнути системні сповіщення
            </button>
          ) : null}

          {showGlobalDisable ? (
            <button
              type="button"
              className="feature-notification-preferences__reset-button feature-notification-preferences__danger-button"
              onClick={() => {
                setDisableAllConfirmOpen(true);
                setRevokeConfirmDeviceId(null);
              }}
              disabled={anyPushBusy}
              data-web-push-disable="true"
              data-web-push-disable-scope="all-devices"
              aria-expanded={disableAllConfirmOpen}
            >
              Вимкнути системні сповіщення на всіх пристроях
            </button>
          ) : null}
        </div>

        {disableAllConfirmOpen ? (
          <div
            className="feature-notification-preferences__confirm"
            role="alertdialog"
            aria-labelledby="notif-disable-all-title"
            aria-describedby="notif-disable-all-desc"
            data-testid="notif-disable-all-confirm"
          >
            <p id="notif-disable-all-title" className="feature-notification-preferences__confirm-title">
              Вимкнути на всіх пристроях?
            </p>
            <p id="notif-disable-all-desc" className="feature-notification-preferences__confirm-desc">
              Системні сповіщення буде вимкнено на всіх підключених пристроях.
              Дозвіл браузера може лишатися наданим.
            </p>
            <div className="feature-notification-preferences__confirm-actions">
              <button
                type="button"
                className="feature-notification-preferences__reset-button"
                onClick={() => setDisableAllConfirmOpen(false)}
                disabled={anyPushBusy}
              >
                Скасувати
              </button>
              <button
                type="button"
                className="feature-notification-preferences__danger-button"
                onClick={() => void handleDisablePush()}
                disabled={anyPushBusy}
                data-testid="notif-disable-all-confirm-action"
              >
                {pushBusy ? "Вимкнення…" : "Вимкнути на всіх пристроях"}
              </button>
            </div>
          </div>
        ) : null}

        {showRecoveryHint ? (
          <Text as="p" size="sm" tone="secondary" data-web-push-recovery="true">
            {recoveryCopy}
          </Text>
        ) : null}

        {masterEnabled ? (
          <div className="feature-notification-preferences__push-categories">
            <Text as="div" size="sm" weight="semibold" tone="primary">
              Які системні сповіщення отримувати
            </Text>
            {WEB_PUSH_CATEGORY_FIELDS.map((field) => (
              <NotificationPreferenceField
                key={field.name}
                name={field.name}
                label={field.label}
                description=""
                checked={Boolean(values[field.name])}
                disabled={categoriesDisabled}
                onToggle={handleToggle}
              />
            ))}
          </div>
        ) : null}

        {(activeDevices.length > 0 || inactiveDevices.length > 0) ? (
          <div
            className="feature-notification-preferences__devices"
            data-feature="notification-devices"
          >
            <Text as="div" size="sm" weight="semibold" tone="primary">
              Пристрої для сповіщень
            </Text>
            {activeDevices.length > 0 ? (
              <ul
                className="feature-notification-preferences__device-list"
                data-device-list="active"
              >
                {activeDevices.map((device) => {
                  const rowBusy = revokingDeviceIds.has(device.id);
                  const confirming = revokeConfirmDeviceId === device.id;
                  // Device A revoke must not disable device B revoke controls.
                  // Only global pushBusy (enable/disable-all) freezes all rows.
                  const revokeTriggerDisabled = rowBusy || pushBusy;
                  return (
                    <li
                      key={device.id}
                      className="feature-notification-preferences__device-item feature-notification-preferences__device-item--active"
                      data-device-active="true"
                    >
                      {renderDeviceLabel(device)}
                      <button
                        type="button"
                        className="feature-notification-preferences__danger-button"
                        onClick={() => handleRevokeDeviceRequest(device.id)}
                        disabled={revokeTriggerDisabled}
                        aria-expanded={confirming}
                        data-testid={`notif-revoke-device-${device.id}`}
                      >
                        {rowBusy ? "Відкликання…" : "Відкликати"}
                      </button>
                      {confirming ? (
                        <div
                          className="feature-notification-preferences__confirm"
                          role="alertdialog"
                          aria-label={`Відкликати пристрій ${device.device_label || device.id}`}
                          data-testid={`notif-revoke-confirm-${device.id}`}
                        >
                          <p className="feature-notification-preferences__confirm-desc">
                            Відкликати цей пристрій? Він перестане отримувати
                            системні сповіщення.
                          </p>
                          <div className="feature-notification-preferences__confirm-actions">
                            <button
                              type="button"
                              className="feature-notification-preferences__reset-button"
                              onClick={() => setRevokeConfirmDeviceId(null)}
                              disabled={rowBusy}
                            >
                              Скасувати
                            </button>
                            <button
                              type="button"
                              className="feature-notification-preferences__danger-button"
                              onClick={() => void handleRevokeDeviceConfirm()}
                              disabled={rowBusy}
                              data-testid={`notif-revoke-confirm-action-${device.id}`}
                            >
                              {rowBusy ? "Відкликання…" : "Відкликати"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Text as="p" size="sm" tone="secondary" data-device-list="active-empty">
                Немає активних пристроїв для сповіщень.
              </Text>
            )}

            {inactiveDevices.length > 0 ? (
              <details
                className="feature-notification-preferences__inactive-devices"
                data-device-list="inactive"
              >
                <summary>
                  Раніше підключені пристрої ({inactiveDevices.length})
                </summary>
                <ul className="feature-notification-preferences__device-list">
                  {inactiveDevices.map((device) => (
                    <li
                      key={device.id}
                      className="feature-notification-preferences__device-item feature-notification-preferences__device-item--inactive"
                      data-device-active="false"
                    >
                      {renderDeviceLabel(device)}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}

        {pushError ? (
          <InlineError className="feature-notification-preferences__message">
            {pushError}
          </InlineError>
        ) : null}
        {pushMessage ? (
          <Text as="span" size="xs" tone="success">
            {pushMessage}
          </Text>
        ) : null}
      </section>

      <div className="feature-notification-preferences__messages">
        {saveError ? (
          <InlineError className="feature-notification-preferences__message">
            {saveError}
          </InlineError>
        ) : null}
        {saveState === "saved" && !dirty ? (
          <Text
            as="span"
            size="xs"
            tone="success"
            className="feature-notification-preferences__saved"
          >
            Налаштування збережено.
          </Text>
        ) : null}
      </div>

      <div className="feature-notification-preferences__actions">
        <button
          type="button"
          className="feature-notification-preferences__reset-button"
          onClick={handleReset}
          disabled={!dirty || saving || anyPushBusy}
        >
          Скасувати зміни
        </button>
        <button
          type="submit"
          className="feature-notification-preferences__save-button"
          disabled={!dirty || saving || anyPushBusy}
          data-loading={saving ? "true" : undefined}
        >
          {saving ? (
            <Spinner size="sm" label="Збереження" />
          ) : (
            "Зберегти зміни"
          )}
        </button>
      </div>
    </form>
  );
}

export default NotificationPreferencesForm;
